import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
    getMotivationInfoThunk,
    getOrderAmountListThunk,
    getRouteInfoThunk,
    getRouteMyRoutesThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";
import { displayDateFormat } from "tm-shared/constants";
import { Schedule } from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
    getSchedulerPointScheduleDetailsManagerThunk,
} from "./manager.slice.thunks";
import { ActiveSchedule, ManagerState } from "./manager.slice.types";

const initialState: ManagerState = {
    team: null,
    routeList: {},
    currentScheduleList: [],
    checkoutAgentsList: [],
    searchAgent: "",
    currentRoute: "",
    checkoutTotals: {
        opened: 0,
        closed: 0,
    },

    mmlStatistics: null,
    mmlStatisticsDate: "",
    mmlStatisticsError: "",
    motivation: null,
    motivationError: "",
    salary: null,
    salaryError: "",
    motivationInfoDate: "",
    lastUpdated: {
        checkouts: null,
        team: null,
        activeSchedule: null,
    },
    activeCollapse: null,
    members: {},
    activeSchedule: null,
};

export const managerSlice = createSlice({
    name: "sv",
    initialState,
    reducers: {
        setSearchAgent(state, action: PayloadAction<string>) {
            state.searchAgent = action.payload;
        },
        setCurrentRoute(state, action: PayloadAction<string>) {
            state.currentRoute = action.payload;
        },
        setCurrentScheduleList(state, action: PayloadAction<Schedule[]>) {
            state.currentScheduleList = action.payload;
        },
        clearCurrentScheduleList(state) {
            state.currentScheduleList = [];
        },
        updateSchedule(
            state,
            action: PayloadAction<{
                pointId: string;
                updatedData: Partial<Schedule>;
            }>,
        ) {
            const { pointId, updatedData } = action.payload;
            const currentRoute = state.currentScheduleList.find(
                (route) => route.pointId === pointId,
            );

            if (currentRoute) {
                Object.assign(currentRoute, updatedData);
            }
        },
        deleteSchedules(state, action: PayloadAction<string[]>) {
            const pointIds = action.payload;
            state.currentScheduleList = state.currentScheduleList.filter(
                (route) => !pointIds.includes(route.pointId),
            );
        },

        setSVActiveCollapse: (state, action: PayloadAction<string | null>) => {
            state.activeCollapse = action.payload;
        },

        setSVActiveSchedule: (
            state,
            action: PayloadAction<ActiveSchedule | null>,
        ) => {
            state.activeSchedule = action.payload;
            state.lastUpdated.activeSchedule = dayjs().format(
                "DD.MM.YYYY HH:mm:ss",
            );
        },
        clearSVActiveSchedule: (state) => {
            state.activeSchedule = null;
            state.lastUpdated.activeSchedule = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                getSchedulerPointScheduleDetailsManagerThunk.fulfilled,
                (state, action) => {
                    state.currentScheduleList = action.payload.result;
                },
            )
            .addCase(
                getUserTeamGetByManagerThunk.fulfilled,
                (state, action) => {
                    state.team = action.payload.result;
                    state.lastUpdated.team = dayjs().format(
                        displayDateFormat.dateTimeSeconds,
                    );
                },
            )
            .addCase(getRouteMyRoutesThunk.fulfilled, (state, { payload }) => {
                state.routeList = arrayToRecord(payload.result, "uuid");
            })

            .addCase(getOrderAmountListThunk.fulfilled, (state, action) => {
                state.checkoutAgentsList = action.payload.result;
                state.checkoutTotals = action.payload.result.reduce(
                    (totals, item) => {
                        totals.opened += item.amountOpened || 0;
                        totals.closed += item.amountClosed || 0;
                        return totals;
                    },
                    { opened: 0, closed: 0 },
                );
                state.lastUpdated.checkouts = dayjs().format(
                    displayDateFormat.dateTimeSeconds,
                );
            })

            .addCase(getKpiGetProgressSvThunk.fulfilled, (state, action) => {
                state.motivation = action.payload.result;

                state.motivationError = "";
            })

            .addCase(getKpiGetProgressSvThunk.rejected, (state) => {
                state.motivationError =
                    "План мотивації наразі не розраховано! Для уточнення зверніться до керівника";
                state.motivation = null;
            })

            .addCase(getKpiGetSalaryToPaySvThunk.fulfilled, (state, action) => {
                state.salary = action.payload.result;
                state.salaryError = "";
            })
            .addCase(getKpiGetSalaryToPaySvThunk.rejected, (state) => {
                state.salaryError =
                    "Зарплатні дані наразі відсутні! Для уточнення зверніться до керівника";
                state.salary = null;
            })
            .addCase(getMotivationInfoThunk.fulfilled, (state, { payload }) => {
                const result = payload?.result?.state?.groups;

                if (!result) return;
                state.motivationInfoDate = payload?.result?.startDate;
            })
            .addCase(getRouteInfoThunk.fulfilled, (state, { payload }) => {
                const route = payload.result;

                state.routeList[route.uuid] = route;
            });
    },
});

export const {
    setCurrentScheduleList,
    clearCurrentScheduleList,
    updateSchedule,
    deleteSchedules,
    setSearchAgent,
    setCurrentRoute,
    setSVActiveCollapse,
    setSVActiveSchedule,
    clearSVActiveSchedule,
} = managerSlice.actions;

export default managerSlice.reducer;
